import { memo } from "react";
import { StyleSheet } from "react-native-unistyles";
import { FeatureGate } from "@src/services/feature-flags/FeatureGate";
import { FEATURE_FLAGS } from "@src/services/feature-flags/feature-flags";
import { CamerasTabLegacyHeader } from "./CamerasTabLegacyHeader.native";
import { CamerasTabSiteWideHeader } from "./CamerasTabSiteWideHeader.native";
import type { FC, RefObject } from "react";
import type { TextInput } from "react-native";
import type { SortOption } from "@src/features/clips/types/camera";

export type SharedHeaderProps = {
  onSearchPress: () => void;
  sortBy: SortOption;
  onSortChange: (sort: SortOption) => void;
  searchExpanded: boolean;
  searchQuery: string;
  onSearchQueryChange: (q: string) => void;
  searchInputRef: RefObject<TextInput | null>;
};

export type CamerasHeaderProps = SharedHeaderProps & {
  tenantDisplayName: string | null;
  tenantsCount: number;
  onTenantPress: () => void;
};

export type SiteWideCamerasHeaderProps = SharedHeaderProps & {
  selectedSiteName: string | null;
  onHamburgerMenuPress: () => void;
  alarmFilter: "armed" | "disarmed" | null;
  onAlarmFilterToggle: (filter: "armed" | "disarmed") => void;
};

export type CamerasTabHeaderProps = CamerasHeaderProps & SiteWideCamerasHeaderProps;

export const SORT_ACTIONS: { id: SortOption; title: string; iosImage?: string }[] = [
  { id: "site", title: "Site", iosImage: "building.2" },
  { id: "nickname", title: "Nickname", iosImage: "tag" },
  { id: "id", title: "ID", iosImage: "number" },
];

const INPUT_H = 36;

export const styles = StyleSheet.create((theme) => ({
  toolbar: {
    flexShrink: 0,
    paddingHorizontal: theme.spacing["5xl"],
    backgroundColor: theme.colors.background,
  },
  legacyToolbar: {
    paddingVertical: theme.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  searchExpandedToolbar: {
    paddingBottom: theme.spacing.xl,
  },
  toolbarRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.md,
  },
  legacyToolbarRow: {
    borderTopWidth: 0,
    paddingTop: 0,
    paddingBottom: 0,
  },
  siteWideToolbarRow: {
    borderTopWidth: 1,
    borderColor: theme.colors.borderCard,
    paddingTop: theme.spacing["4xl"],
    paddingBottom: theme.spacing.xl,
  },
  searchExpandedToolbarRow: {
    paddingBottom: theme.spacing.xs,
  },
  tenantGroup: { flex: 1, flexDirection: "row", alignItems: "center", gap: theme.spacing.xs, minWidth: 0 },
  tenantSection: { flex: 1, minWidth: 0 },
  labelFilter: {
    paddingHorizontal: theme.spacing.xl,
    height: 36,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
    borderColor: theme.colors.interactive.buttons.default_background,
    borderRadius: theme.spacing["10xl"],
  },
  iconLeading: {
    padding: theme.spacing.sm,
    paddingRight: theme.spacing.xs,
    justifyContent: "center",
    alignItems: "center",
  },
  iconBtn: {
    height: 36,
    aspectRatio: 1,
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.interactive.buttons.default_background,
    backgroundColor: "transparent",
    justifyContent: "center",
    alignItems: "center",
  },
  legacyIconBtn: {
    padding: theme.spacing.sm,
    backgroundColor: "transparent",
    justifyContent: "center",
    alignItems: "center",
  },
  searchBarWrap: {
    paddingTop: theme.spacing.md,
    minHeight: INPUT_H + theme.spacing.md,
    paddingBottom: theme.spacing.xl,
  },
  searchInput: { flex: 1, minHeight: INPUT_H },
}));

export const CamerasTabHeader: FC<CamerasTabHeaderProps> = memo((props) => {
  const { selectedSiteName, onHamburgerMenuPress, ...legacyProps } = props;

  return (
    <FeatureGate flag={FEATURE_FLAGS.SITE_WIDE_CONTROLS} fallback={<CamerasTabLegacyHeader {...legacyProps} />}>
      <CamerasTabSiteWideHeader
        selectedSiteName={selectedSiteName}
        onHamburgerMenuPress={onHamburgerMenuPress}
        alarmFilter={props.alarmFilter}
        onAlarmFilterToggle={props.onAlarmFilterToggle}
        onSearchPress={props.onSearchPress}
        sortBy={props.sortBy}
        onSortChange={props.onSortChange}
        searchExpanded={props.searchExpanded}
        searchQuery={props.searchQuery}
        onSearchQueryChange={props.onSearchQueryChange}
        searchInputRef={props.searchInputRef}
      />
    </FeatureGate>
  );
});

CamerasTabHeader.displayName = "CamerasTabHeader";
